import type { EventDefinition } from '../core/types';

export const eventDefinitions: EventDefinition[] = [
  {
    id: 'buried_lockbox',
    act: 1,
    title: "The Sealed Consignment",
    body: "A locked consignment crate lies beneath a fallen brace. Its Ichor cylinders are intact and sealed. The bent steel shipping frame could keep the next outpost supplied.",
    choices: [
      {
        id: 'force',
        label: "Pry out the frame",
        outcome: "A sprung brace cuts your arm. You recover the valuable steel without opening a cylinder.",
        effects: [
          { kind: 'hp', amount: -7 },
          { kind: 'iron', amount: 28 },
        ],
      },
      {
        id: 'salvage',
        label: "Strip the outer fittings",
        outcome: "The safer salvage yields less Iron, but its spare seals restore your Gear.",
        effects: [
          { kind: 'iron', amount: 12 },
          { kind: 'repair_all' },
        ],
      },
      { id: 'leave', label: "Mark it for recovery", outcome: "You record the location and leave the containment untouched.", effects: [] },
    ],
  },
  {
    id: 'last_lamplighter',
    act: 1,
    title: "The Last Surveyor",
    body: "The last surveyor has kept a dry shelter open beside the line. She offers a practical choice: lighten your kit, or help brace her roof in exchange for a lesson.",
    choices: [
      {
        id: 'memory',
        label: "Retire a technique",
        outcome: "She helps you set aside a habit you no longer need. Remove one card.",
        effects: [{ kind: 'choose_remove' }],
      },
      {
        id: 'blood',
        label: "Brace the shelter",
        outcome: "The work costs skin and breath. Her field lesson sharpens one technique.",
        effects: [
          { kind: 'hp', amount: -6 },
          { kind: 'choose_upgrade' },
        ],
      },
    ],
  },
  {
    id: 'railside_shrine',
    act: 1,
    title: "The Silent Sword",
    body: "Two evacuees have planted a symbolic Silence Sword beside the rail. In the Isles, whoever holds such a sword is given room to speak. They offer you that quiet before the next climb.",
    choices: [
      {
        id: 'hammer',
        label: "Stay and listen",
        outcome: "Food, rest, and an honest conversation steady you for the road. The sword itself grants no magic.",
        effects: [
          { kind: 'max_hp', amount: 4 },
          { kind: 'hp', amount: 4 },
        ],
      },
      {
        id: 'collect',
        label: "Carry their spare bracing",
        outcome: "They give you scrap from the dismantled shelter. Leave the Silence Sword where it stands.",
        effects: [{ kind: 'iron', amount: 18 }],
      },
    ],
  },
  {
    id: 'kiln_choir',
    act: 2,
    title: "The Primer Relay",
    body: "An emergency primer relay is hammering against its stops. The raw-Ichor line is sealed, but its hot drive housing must be isolated before the station can evacuate.",
    choices: [
      {
        id: 'join',
        label: "Recover the primer pump",
        outcome: "You free the pump and burn your palms on its housing. Its sealed reservoir remains intact.",
        effects: [
          { kind: 'hp', amount: -10 },
          { kind: 'add_relic', relicId: 'spare_bellows' },
        ],
      },
      {
        id: 'silence',
        label: "Disconnect the drive",
        outcome: "The relay stops. Spare mounting steel can now be recovered safely.",
        effects: [{ kind: 'iron', amount: 25 }],
      },
    ],
  },
  {
    id: 'slag_orchard',
    act: 2,
    title: "The Dry-Purge Cabinet",
    body: "Inside a dry-purge cabinet, a rack of unused breathing filters sits beside salvaged tools. A jammed safety shutter is all that stands between you and a less punishing field routine.",
    choices: [
      {
        id: 'eat',
        label: "Force the safety shutter",
        outcome: "The shutter bruises you. A clean filter and a discarded bad habit make the next breath easier.",
        effects: [
          { kind: 'hp', amount: -5 },
          { kind: 'choose_remove' },
          { kind: 'max_hp', amount: 3 },
        ],
      },
      {
        id: 'harvest',
        label: "Recover the outer rack",
        outcome: "You leave the sealed cabinet alone and take its spare external fittings.",
        effects: [{ kind: 'iron', amount: 22 }],
      },
    ],
  },
  {
    id: 'runaway_crucible',
    act: 2,
    title: "Runaway Transfer Cart",
    body: "A transfer cart is rolling in circles on a broken switch. Its cylinders are sealed, but the motor is hot and the brakes are gone. A dry catch bay lies beside the track.",
    choices: [
      {
        id: 'catch',
        label: "Set the manual brake",
        outcome: "The hot handle scorches you. The recovered cart carries spare parts and repair supplies.",
        effects: [
          { kind: 'hp', amount: -8 },
          { kind: 'iron', amount: 30 },
          { kind: 'repair_all' },
        ],
      },
      {
        id: 'guide',
        label: "Divert it into the catch bay",
        outcome: "The cart settles against the buffer. A relieved orderly treats your wounds while the line is quiet.",
        effects: [{ kind: 'hp', amount: 14 }],
      },
    ],
  },
  {
    id: 'mirror_press',
    act: 3,
    title: "The Pattern Bench",
    body: "A pattern bench holds abandoned dies and the custodian's correction notes. These are ordinary workshop records, not a machine that creates matter without a Catalurgist.",
    choices: [
      {
        id: 'accept',
        label: "Study a correction",
        outcome: "A measured adjustment makes one technique more effective.",
        effects: [{ kind: 'choose_upgrade' }],
      },
      {
        id: 'break',
        label: "Salvage the ruined die",
        outcome: "The fractured die has no further use here. Its steel does.",
        effects: [{ kind: 'iron', amount: 32 }],
      },
    ],
  },
  {
    id: 'borrowed_voice',
    act: 3,
    title: "The Calibration Offer",
    body: "A stranded technician offers a calibration lens. It was tuned for a harsher load than your harness can safely carry. She explains the permanent strain before offering the exchange.",
    choices: [
      {
        id: 'trade',
        label: "Accept the strain",
        outcome: "The lens sharpens your timing, but the altered harness leaves less room for recovery. Lose maximum Health.",
        effects: [
          { kind: 'max_hp', amount: -7 },
          { kind: 'add_relic', relicId: 'slagglass_eye' },
        ],
      },
      { id: 'refuse', label: "Keep the safe setting", outcome: "She nods, leaves the calibration alone, and points out the next isolation valve.", effects: [] },
    ],
  },
  {
    id: 'white_vein',
    act: 3,
    title: "The Chilled Line",
    body: "A closed coolant loop crosses the corridor beside a separate, sealed Genesis supply line. Frost clouds the coolant housing. Work on the coolant only; water must never reach the raw Ichor.",
    choices: [
      {
        id: 'cut',
        label: "Isolate and recover the cooler",
        outcome: "A sharp housing edge catches your hand. The recovered closed-loop cooler will help manage Heat.",
        effects: [
          { kind: 'hp', amount: -9 },
          { kind: 'add_relic', relicId: 'quenching_oil' },
        ],
      },
      {
        id: 'cauterize',
        label: "Repair the coolant bypass",
        outcome: "The independent cooling circuit settles. You use the quiet to service your Gear and dress your wounds.",
        effects: [
          { kind: 'repair_all' },
          { kind: 'hp', amount: 8 },
        ],
      },
    ],
  },
];
